'use client'

import { useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import Button from 'src/components/design-system/Button'
import { CustomSelect } from 'src/components/design-system/form/CustomSelect'
import LineChart from './LineChart'

// Kalendarz korzysta z window, więc jak w wyszukiwarce zaawansowanej ładujemy go tylko w przeglądarce.
const DatePickerRange = dynamic(() => import('src/components/design-system/form/DatePickerRange'), { ssr: false })

const MAX_MONTHS = 120

const monthsBetween = (from, to) => {
  const [fromYear, fromMonth] = from.split('-').map(Number)
  const [toYear, toMonth] = to.split('-').map(Number)
  return (toYear - fromYear) * 12 + (toMonth - fromMonth)
}

const toIsoDate = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime())
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    : ''

const todayIso = () => toIsoDate(new Date())

const formatMonth = (month) => {
  const [year, number] = month.split('-')
  return `${number}.${year}`
}

// Wykres liczby publikacji w czasie z filtrami: czasopismo / wydawnictwo oraz zakres miesięcy (od - do).
export default function StatisticsOverTimeClient({ type, initial, options, labels, title }) {
  const articles = type === 'articles'
  const [selected, setSelected] = useState('')
  // Wykres jest miesięczny, więc kalendarz wybiera dni, a do API trafia miesiąc wybranej daty (pusta data = domyślna).
  const [dates, setDates] = useState(() => ({ from: `${initial.from}-01`, to: todayIso() }))
  const [resetKey, setResetKey] = useState(0)
  const from = dates.from ? dates.from.slice(0, 7) : initial.from
  const to = dates.to ? dates.to.slice(0, 7) : initial.to
  const [data, setData] = useState(initial)
  const [state, setState] = useState('idle')
  const first = useRef(true)

  const validRange = Boolean(from && to) && from <= to && monthsBetween(from, to) < MAX_MONTHS

  useEffect(() => {
    // Pierwszy render ma dane z serwera.
    if (first.current) {
      first.current = false
      return undefined
    }
    if (!validRange) return undefined

    const controller = new AbortController()
    const query = new URLSearchParams({ type, from, to })
    if (selected) query.set(articles ? 'journal' : 'publisher', selected)
    setState('loading')
    fetch(`/api/statistics-over-time?${query}`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Request failed')
        return response.json()
      })
      .then((result) => {
        setData(result)
        setState('idle')
      })
      .catch((error) => {
        if (error.name !== 'AbortError') setState('error')
      })

    return () => controller.abort()
  }, [type, articles, selected, from, to, validRange])

  const reset = () => {
    setSelected('')
    setDates({ from: `${initial.from}-01`, to: todayIso() })
    // Select i kalendarz są niekontrolowane - zmiana klucza przywraca ich wartości domyślne.
    setResetKey((key) => key + 1)
  }
  const changeDate = (date, type) => setDates((current) => ({ ...current, [type]: toIsoDate(date) }))
  const series = articles ? labels.articlesSeries : labels.monographsSeries
  const points = data?.months || []

  return (
    <div>
      <div className="grid gap-24 pb-24 d:grid-cols-3" key={resetKey}>
        <CustomSelect
          name={`over-time-${type}`}
          label={articles ? labels.chooseJournal : labels.choosePublisher}
          options={[{ value: '', label: articles ? labels.allJournals : labels.allPublishers }, ...options]}
          placeholder={articles ? labels.allJournals : labels.allPublishers}
          defaultValue={selected}
          onChange={(event) => setSelected(event?.target?.value || '')}
          searchable
          searchPlaceholder={articles ? labels.chooseJournal : labels.choosePublisher}
        />
        <DatePickerRange
          label={labels.range}
          name={`over-time-${type}-range`}
          className="d:col-span-2"
          fieldStyle
          startDate={dates.from}
          endDate={dates.to}
          onChange={changeDate}
        />
      </div>
      <div className="pb-24">
        <Button variant="light" size="s" onClick={reset}>
          {labels.reset}
        </Button>
      </div>
      <p className="text-small">{series}</p>
      <div aria-live="polite" aria-busy={state === 'loading'}>
        {!validRange ? (
          <p role="alert">{labels.invalidRange}</p>
        ) : state === 'error' ? (
          <p role="alert">{labels.error}</p>
        ) : points.length ? (
          <div style={{ opacity: state === 'loading' ? 0.5 : 1 }}>
            <LineChart points={points} title={`${title} (${labels.count})`} formatMonth={formatMonth} />
            <details>
              <summary>{labels.count}</summary>
              <table>
                <thead>
                  <tr>
                    <th>{labels.month}</th>
                    <th>{labels.count}</th>
                  </tr>
                </thead>
                <tbody>
                  {points.map((point) => (
                    <tr key={point.month}>
                      <td>{formatMonth(point.month)}</td>
                      <td>{point.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </div>
        ) : (
          <p>{labels.empty}</p>
        )}
        {state === 'loading' && <span className="sr-only">{labels.loading}</span>}
      </div>
    </div>
  )
}
